import {
  GRID_TEMPLATE,
} from "@/routes/app/contacts/contactsTableLayout";
import { Skeleton } from "@/shared/components/ui/Skeleton";
import { cn } from "@/shared/lib/utils";

interface ContactsTableSkeletonProps {
  /** Number of placeholder rows to render. */
  rows?: number;
}

/**
 * Loading placeholder for the contacts table. Per design.md §14 it keeps the
 * real static chrome — the same container and column header — and replaces
 * only the dynamic rows with pulsing glass skeletons. It reuses the shared
 * `GRID_TEMPLATE` so the skeleton columns line up with the real rows and the
 * responsive cell visibility matches (`hidden md:block` / `lg:block` /
 * `xl:block`). No row links — there's nothing to navigate to yet.
 */
export function ContactsTableSkeleton({ rows = 8 }: ContactsTableSkeletonProps) {
  return (
    <div
      data-testid="contacts-table-skeleton"
      className="overflow-hidden rounded-xl border border-border-muted glass-surface-subtle"
    >
      <div
        className={cn(
          "grid items-center gap-4 border-b border-border-muted px-4 py-2.5",
          "type-label-sm tracking-wider uppercase text-text-muted",
          GRID_TEMPLATE,
        )}
      >
        <span className="flex items-center">
          <Skeleton className="h-4 w-4" />
        </span>
        <span>Name</span>
        <span className="hidden md:block">Email</span>
        <span className="hidden lg:block">Phone</span>
        <span className="hidden md:block">Address</span>
        <span className="hidden xl:block">Tags</span>
      </div>
      <ul className="flex flex-col">
        {Array.from({ length: rows }).map((_, i) => (
          <li
            key={i}
            className={cn(
              "grid items-center gap-4 border-t border-border-muted px-4 py-2.5 first:border-t-0",
              GRID_TEMPLATE,
            )}
          >
            <span className="flex items-center">
              <Skeleton className="h-4 w-4" />
            </span>
            <Skeleton shape="text" className="w-32" />
            <Skeleton shape="text" className="hidden w-40 md:block" />
            <Skeleton shape="text" className="hidden w-24 lg:block" />
            <Skeleton shape="text" className="hidden w-44 md:block" />
            <Skeleton shape="text" className="hidden w-20 xl:block" />
          </li>
        ))}
      </ul>
    </div>
  );
}
